import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { cn } from "../lib/cn";
import { spring } from "../lib/springs";

// ---------------------------------------------------------------------------
// Ported from Fluid Functionalism's fluid-hover-highlight.
//
// The one hover highlight every fluid hover list renders: an absolutely
// positioned fill that springs between the rects `useFluidHover` measures.
//
//   const hover = useFluidHover(ref);
//   <FluidHoverHighlight hover={hover} className={shape.bg} />
//
// It owns no layout opinion beyond `absolute`: radius, z-index, and the
// offsetParent (the container must be `position: relative`) are the
// consumer's. Rows sit above it with `position: relative; z-index: 10`.
//
// Props (either `hover`, or `rect` + `session`):
//   hover      the hook's return value. The highlight sits on
//              `itemRects[activeIndex]` once `isMeasured`, and re-keys on
//              the session.
//   hidden     with `hover`: keep the list's state but show nothing (a closed
//              popup, hover switched off). Runs the exit fade.
//   rect       for lists that resolve their own rect: the rect to sit on, in
//              the container's coordinate space. `null` hides the highlight.
//   session    with `rect`: `session` from `useFluidHover`, so a fresh pointer
//              entry fades in instead of sliding over.
//   from       where a fresh session fades in from (a dropdown's checked row,
//              a nav menu's active route). Defaults to the rect itself.
//   fadeIn     `false` starts a fresh session opaque instead of fading in.
//   className  radius, z-index, anything else; merged onto the base class.
//   transition the positional spring. Defaults to `spring.fast`. `false`
//              snaps to the new rect with no travel (a layout reflow that
//              moved the rows, not a hover change). The opacity fade is
//              always 0.08s.
// ---------------------------------------------------------------------------

const fade = { duration: 0.08 };
const snap = { duration: 0 };

/** A measured rect as animation targets: position as a transform, size as
 *  layout. */
export function toTarget(rect) {
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

/**
 * Resolves the positional transition. Reduced motion keeps the opacity fade
 * and drops the travel, per the motion guidelines: fewer and gentler, not
 * none.
 */
export function resolveHighlightTransition(transition, reduceMotion) {
  const positional =
    transition === false || reduceMotion ? snap : (transition ?? spring.fast);
  return { ...positional, opacity: fade };
}

/** The rect and session a set of props resolves to. */
export function resolveHighlightSource(props) {
  if (props.hover) {
    const { activeIndex, itemRects, isMeasured, session } = props.hover;
    const rect =
      !props.hidden && isMeasured && activeIndex !== null
        ? (itemRects[activeIndex] ?? null)
        : null;
    return { rect, session };
  }
  return { rect: props.rect, session: props.session };
}

export function FluidHoverHighlight(props) {
  const { from, fadeIn = true, className, transition } = props;
  const { rect, session } = resolveHighlightSource(props);
  // Reads the OS media query directly, so it honours reduced motion even
  // outside the app's MotionConfig. Inside it the result is the same twice
  // over: the travel is a transform, which MotionConfig reduces too.
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <AnimatePresence>
      {rect && (
        <motion.div
          key={session}
          data-slot="fluid-hover-highlight"
          // Pinned to the container's padding corner and moved with a
          // transform, so the travel runs on the compositor instead of
          // re-laying out every frame. Width and height are real layout
          // values, but they only change when the target rect's size does,
          // which in most lists is never.
          className={cn("ff-hover-highlight", className)}
          initial={{ opacity: fadeIn ? 0 : 1, ...toTarget(from ?? rect) }}
          animate={{ opacity: 1, ...toTarget(rect) }}
          exit={{ opacity: 0, transition: spring.fast.exit }}
          transition={resolveHighlightTransition(transition, reduceMotion)}
        />
      )}
    </AnimatePresence>
  );
}
